import { useState } from 'react'

import {
  IncidentsFilterDrawerMode,
  useDrawerMode,
} from '../../hooks/useDrawerMode'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, Drawer } from '@hivewatch/dark'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { buildFilterCriteria } from '@/redux/incidents/conversion'
import {
  selectActiveFilter,
  setActiveFilterCriteria,
} from '@/redux/incidents/incidentsSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

import { IncidentFiltersFormInputsI } from '../../types/form'
import { removeEmptyFilters } from '../../utils/utils'
import { DRAWER_Z_INDEX } from '../constants/constants'
import { DarkDeactivateFilterConfirmationModal } from './DarkDeactivateFilterConfirmationModal'
import { DarkIncidentsFilterForm } from './DarkIncidentsFilterForm'
import { DarkIncidentsFilterList } from './DarkIncidentsFilterList'

const DRAWER_WIDTH = '400px'

const FILTER_FIELD_NAMES: Array<keyof IncidentFiltersFormInputsI> = [
  'facilityName',
  'floorName',
  'deviceName',
  'createdAt',
  'incidentStatus',
  'sourceType',
  'incidentType',
  'priority',
  'mutedStatus',
  'clips',
  'aiOperatorState',
]

interface DarkIncidentsFilterDrawerIProps {
  isOpen: boolean
  onClose: () => void
}

export const DarkIncidentsFilterDrawer = ({
  isOpen,
  onClose,
}: DarkIncidentsFilterDrawerIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const dispatch = useAppDispatch()
  const activeFilter = useAppSelector(selectActiveFilter)
  const isIncidentTypeDefinition = useFeatureFlag(
    FeatureFlag.useIncidentTypeDefinition
  )

  const { drawerMode, goBack, openFilterList, openFilterUpdate, editedFilter } =
    useDrawerMode({ onDrawerClose: onClose })

  // The form methods live at the drawer level so the sticky footer (Clear /
  // Apply) and the sub-view body share one form instance. Per-mode form
  // internals (option loading, reset-on-defaults) stay in the form component,
  // which remounts per sub-view exactly like the legacy drawer.
  const methods = useForm<IncidentFiltersFormInputsI>({
    reValidateMode: 'onChange',
  })

  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false)

  const onSubmit: SubmitHandler<IncidentFiltersFormInputsI> = (values) => {
    dispatch(
      setActiveFilterCriteria(
        buildFilterCriteria(values, isIncidentTypeDefinition)
      )
    )
    onClose()
    trackEvent('Incidents View: Filters Applied', {
      filters: removeEmptyFilters(values),
    })
  }

  const applyFilters = methods.handleSubmit(onSubmit)

  const clearAllFilters = () => {
    FILTER_FIELD_NAMES.forEach((fieldName) => methods.setValue(fieldName, null))
    trackEvent(`${BUTTON_PRESS} Incidents Drawer - Clear Filters`)
  }

  const drawerTitle =
    drawerMode === IncidentsFilterDrawerMode.DETAIL
      ? t('incidents:filter_incidents_title')
      : drawerMode === IncidentsFilterDrawerMode.LIST
      ? t('incidents:filter_saved_title')
      : editedFilter?.name ?? ''

  return (
    <Drawer
      colorScheme={colorScheme}
      footer={
        drawerMode === IncidentsFilterDrawerMode.DETAIL ? (
          <>
            <Button
              colorScheme={colorScheme}
              onClick={clearAllFilters}
              variant='secondary'
            >
              {t('common:clear')}
            </Button>
            <Button
              colorScheme={colorScheme}
              onClick={() => {
                if (activeFilter) {
                  setIsDeactivateModalOpen(true)
                } else {
                  void applyFilters()
                }
              }}
              variant='primary'
            >
              {t('incidents:filter_apply')}
            </Button>
          </>
        ) : undefined
      }
      headerActions={
        drawerMode === IncidentsFilterDrawerMode.DETAIL ? (
          <Button
            colorScheme={colorScheme}
            onClick={openFilterList}
            variant='secondary'
          >
            {t('incidents:filter_view_saved')}
          </Button>
        ) : undefined
      }
      isOpen={isOpen}
      onBack={goBack}
      onClose={onClose}
      title={drawerTitle}
      width={DRAWER_WIDTH}
      zIndex={DRAWER_Z_INDEX}
    >
      {drawerMode === IncidentsFilterDrawerMode.LIST ? (
        <DarkIncidentsFilterList
          colorScheme={colorScheme}
          onFilterUpdate={openFilterUpdate}
        />
      ) : (
        <DarkIncidentsFilterForm
          colorScheme={colorScheme}
          drawerMode={drawerMode}
          editedFilter={editedFilter}
          key={drawerMode}
          methods={methods}
          onGoBack={goBack}
        />
      )}
      {isDeactivateModalOpen && activeFilter && (
        <DarkDeactivateFilterConfirmationModal
          activeFilter={activeFilter}
          colorScheme={colorScheme}
          isOpen={isDeactivateModalOpen}
          onApplyFilters={() => {
            void applyFilters()
          }}
          onClose={() => setIsDeactivateModalOpen(false)}
        />
      )}
    </Drawer>
  )
}
